import { Add, Upload } from "@mui/icons-material";
import { Autocomplete, Fab, Stack, TextField, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { parseAsArrayOf, parseAsBoolean, parseAsString, parseAsStringEnum } from "nuqs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { GqlFieldworkerTransportationMethod } from "~/__generated__";
import GaiaDataList from "~/modules/app/components/GaiaDataList";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import GaiaTableLinkCell from "~/modules/app/components/GaiaTableLinkCell";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";
import CheckboxInput from "~/shared/components/CheckboxInput";
import SearchInput from "~/shared/components/SearchInput";
import { useDebouncedValue } from "~/shared/hooks/useDebouncedValue";
import { useGridQueryState } from "~/shared/hooks/useGridQueryState";

import UserBadge from "../../components/UserBadge";
import UserCourseSelect from "../../components/UserCourseSelect";
import useUserListScreen_GetAll from "./api/useUserListScreen_GetAll";

type TransportationOption = { label: string; value: GqlFieldworkerTransportationMethod };

export default function UserListScreen() {
    const { t } = useTranslation("user");

    const transportationOptions: TransportationOption[] = [
        { value: GqlFieldworkerTransportationMethod.Mywheels, label: t("fieldworkerForm.labelIsMywheelsUser") },
        { value: GqlFieldworkerTransportationMethod.OwnTransport, label: t("fieldworkerForm.labelHasOwnTransport") },
    ];

    const [queryState, setQueryState, gridModelProps] = useGridQueryState({
        q: parseAsString.withDefault(""),
        courseId: parseAsArrayOf(parseAsString).withDefault([]),
        transportation: parseAsArrayOf(
            parseAsStringEnum<GqlFieldworkerTransportationMethod>(Object.values(GqlFieldworkerTransportationMethod)),
        ).withDefault([]),
        isActiveFieldworker: parseAsBoolean.withDefault(false),
        finishedOnboarding: parseAsBoolean.withDefault(false),
    });

    const debouncedQ = useDebouncedValue(queryState.q);

    const params = useMemo(
        () => ({
            q: debouncedQ || undefined,
            courseId: queryState.courseId ?? [],
            transportation: queryState.transportation ?? [],
            isActiveFieldworker: queryState.isActiveFieldworker,
            finishedOnboarding: queryState.finishedOnboarding,
            page: queryState.page,
            pageSize: queryState.pageSize,
            sortBy: queryState.sortBy,
            sortOrder: queryState.sortOrder,
        }),
        [debouncedQ, queryState],
    );

    const { data, loading, error, totalRows } = useUserListScreen_GetAll(params);

    const handleParameterChange = <T,>(key: string, value: T) => {
        setQueryState({ [key]: value, page: 0 } as Parameters<typeof setQueryState>[0]);
    };

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "firstName",
                headerName: t("userListScreen.colName"),
                renderCell: params => {
                    const { firstName, lastName, profilePictureMedia } = params.row;
                    return <UserBadge names={[firstName, lastName]} profilePicture={profilePictureMedia?.url} showName />;
                },
                flex: 1,
            },
            {
                field: "email",
                headerName: t("userListScreen.colEmail"),
                flex: 1,
            },
            {
                field: "phone",
                headerName: t("userListScreen.colPhone"),
                flex: 1,
            },
            {
                field: "role",
                headerName: t("userListScreen.colRole"),
                valueGetter: (_, row) => row.tenantRole?.name,
                flex: 1,
            },
            {
                type: "actions",
                field: "id",
                headerName: "",
                maxWidth: 40,
                renderCell: props => <GaiaTableLinkCell value={props.value} />,
            },
        ],
        [t],
    );

    if (!data && !loading && error) {
        return <ErrorScreen error={error} />;
    }

    return (
        <GaiaPageLayoutColumn maxWidth="xl">
            <GaiaPageGridComponent
                title={t("userListScreen.title")}
                actions={
                    <>
                        <Tooltip title={t("userListScreen.tooltipNewUser")}>
                            <Fab component={Link} to="../users/new" color="primary">
                                <Add />
                            </Fab>
                        </Tooltip>
                        <Tooltip title={t("userListScreen.tooltipImportUsers")}>
                            <Fab component={Link} to="../users/import" color="primary">
                                <Upload />
                            </Fab>
                        </Tooltip>
                    </>
                }
            >
                <Stack direction="row" gap={1} flexWrap="wrap" alignItems="flex-start" useFlexGap sx={{ "& > *": { flex: 1 } }}>
                    <SearchInput
                        label={t("userListScreen.filterSearch")}
                        value={queryState.q}
                        onChange={value => handleParameterChange("q", value)}
                    />
                    <UserCourseSelect value={queryState.courseId} onChange={value => handleParameterChange("courseId", value)} />
                    <Autocomplete
                        multiple
                        size="small"
                        options={transportationOptions}
                        value={transportationOptions.filter(o => queryState.transportation.includes(o.value))}
                        onChange={(_, newValue) =>
                            handleParameterChange(
                                "transportation",
                                newValue.map(o => o.value),
                            )
                        }
                        getOptionLabel={option => option.label}
                        getOptionKey={option => option.value}
                        renderInput={params => (
                            <TextField
                                {...params}
                                fullWidth
                                label={t("userListScreen.filterTransportation")}
                                variant="outlined"
                            />
                        )}
                    />
                    <CheckboxInput
                        label={t("fieldworkerForm.labelIsActive")}
                        value={queryState.isActiveFieldworker}
                        onChange={value => handleParameterChange("isActiveFieldworker", value)}
                    />
                    <CheckboxInput
                        label={t("fieldworkerForm.labelHasFinishedOnboarding")}
                        value={queryState.finishedOnboarding}
                        onChange={value => handleParameterChange("finishedOnboarding", value)}
                    />
                </Stack>

                <GaiaDataList
                    data={data || []}
                    loading={loading}
                    error={error}
                    totalRows={totalRows || 0}
                    columns={columns}
                    gridModels={gridModelProps}
                />
            </GaiaPageGridComponent>
        </GaiaPageLayoutColumn>
    );
}
